// The environment comes from the URL at runtime — the same built bundle runs
// in every environment with no build-time swap. Pool and client ids are
// public identifiers, not secrets; the host picks the row, and every tenant
// that host serves — seeded or operator-minted — authenticates against the
// pool named in it.

export interface EnvConfig {
  env: 'dev' | 'test' | 'stage' | 'prod' | 'local';
  apiBase: string;
  userPoolId: string;
  platformClientId: string;
}

const BY_HOST: Record<string, EnvConfig> = {
  'dev.supplier.appliedintelligencelabs.gr': {
    env: 'dev',
    apiBase: '/v1',
    userPoolId: 'eu-central-1_MWK95iQ8m',
    platformClientId: '6nt1i705o327jo143vcqfal4hd',
  },
  'stage.supplier.appliedintelligencelabs.gr': {
    env: 'stage',
    apiBase: '/v1',
    userPoolId: 'eu-central-1_MWK95iQ8m', // the shared nonprod pool
    platformClientId: '1f6ulu4g4t2va00hvplcglu39t',
  },
  // Prod, stood up 2026-09-01. Its OWN isolated pool (canon 07/17) — nothing is
  // shared with the nonprod pool above, so a nonprod credential cannot reach a
  // prod tenant even by accident. The client is SRP + magic-link only: unlike
  // dev and stage it carries no ALLOW_ADMIN_USER_PASSWORD_AUTH, which is why the
  // admin-auth demo cannot run here and a prod-safe smoke stands in for it.
  'supplier.appliedintelligencelabs.gr': {
    env: 'prod',
    apiBase: '/v1',
    userPoolId: 'eu-central-1_n14KZY2ua',
    platformClientId: '6j3g9lpm6iriibp9l23noi929l',
  },
  localhost: {
    env: 'local',
    apiBase: 'https://dev.supplier.appliedintelligencelabs.gr/v1',
    userPoolId: 'eu-central-1_MWK95iQ8m',
    platformClientId: '6nt1i705o327jo143vcqfal4hd',
  },
};

export function config(): EnvConfig {
  const c = BY_HOST[window.location.hostname];
  if (!c) throw new Error(`No environment is configured for ${window.location.hostname}`);
  return c;
}

// ISO's tenant id range: 15 digits starting '2'. Hotel and supplier ids are
// both derived into it (api/src/domain/inviteToken.ts).
const ACCOUNT_ID = /^2\d{14}$/;

// The pre-auth step: the account id selects pool and client through this
// runtime map — no deploy-time file swap. One pool per host, so a well-formed
// id resolves to THIS environment's pool and client, whoever the tenant is.
//
// It checks shape, never existence. Tenant ids are derived at provisioning and
// never enumerated, so an operator-minted hotel is one this bundle has never
// heard of by construction — the hard-coded list of seeded ids that used to
// stand here refused every real tenant (B2). A client-side existence verdict
// would also answer "does this account exist?" to anyone probing the form,
// before anything is authenticated. The server decides instead, and discloses
// nothing doing it: every app client sets PreventUserExistenceErrors
// (api/infrastructure/auth.yaml), and POST /v1/auth/code answers 202 with a
// decoy for an unknown identifier (api/src/handlers/auth.ts). This departs
// from canon 17's "an unknown tenant fails as `Account not found`" — that line
// predates ids being derived rather than listed.
export function resolveAccount(accountId: string): EnvConfig | null {
  return ACCOUNT_ID.test(accountId) ? config() : null;
}
